// Runtime cap on user/OS text scaling.
//
// Why this exists: on Android (especially 14+), the system "Font size" setting
// goes up to 2.0× and the WebView applies it as a text-zoom multiplier to all
// text. Our layouts are designed and hardened to a 150% ceiling (see the
// scaling-cap product decision), so beyond that things get cramped. CSS alone
// can't express "allow up to 1.5× then stop" — `text-size-adjust` can only turn
// the OS inflation fully on or off. So we measure the effective text-zoom and,
// when it exceeds the cap, counter-scale the root font-size back down. Because
// the whole type scale is rem-based, the root font-size is a single lever:
// shrinking it by (CAP / measured) yields a net scale of exactly CAP.
//
// iOS web (Safari/WKWebView) does not auto-apply Dynamic Type to web content,
// so on iOS the measured factor is ~1.0 and this is a no-op.
//
// NOTE: detection relies on the WebView reflecting text-zoom in laid-out box
// size; this holds across the Android versions we tested but should be
// re-validated on a real device after any major WebView/OS bump.

const MAX_SCALE = 1.5;
const BASE_PX = 16; // browser default root font-size

// Track whether *we* set the inline root font-size, so we only ever clear our
// own override — never something else (e.g. the dev-only TextScaleDevTool).
let capApplied = false;

/**
 * Measures the effective text-zoom multiplier the platform is applying.
 *
 * A probe with an explicit `font-size` in px and `line-height: 1` has a line
 * box exactly equal to its font-size — independent of font family or glyphs.
 * The OS text-zoom multiplies the used font-size, so the measured box height
 * divided by the specified size IS the multiplier. Crucially, browser *page*
 * zoom (desktop Ctrl/Cmd-+) does NOT change CSS-pixel box measurements, so this
 * isolates text-only zoom and won't fight a user who legitimately zooms the
 * whole page.
 */
function measureTextZoom(): number {
  if (typeof document === 'undefined' || !document.body) return 1;
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;' +
    'margin:0;padding:0;border:0;white-space:nowrap;font-size:100px;line-height:1';
  probe.textContent = 'X';
  document.body.appendChild(probe);
  const measured = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);
  if (!measured || !Number.isFinite(measured)) return 1;
  return measured / 100;
}

/** Re-evaluate the cap and apply (or clear) the root counter-scale. */
export function applyTextScaleCap(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const factor = measureTextZoom();

  // Small epsilon so a measured 1.50 doesn't trigger a pointless counter-scale.
  if (factor > MAX_SCALE + 0.01) {
    root.style.setProperty('font-size', `${(MAX_SCALE / factor) * BASE_PX}px`);
    capApplied = true;
  } else if (capApplied) {
    // Only clear the override we set — leaves any other inline font-size alone.
    root.style.removeProperty('font-size');
    capApplied = false;
  }
}

/**
 * Initialise the cap: apply once immediately, then re-apply whenever the
 * platform text scale might have changed — viewport resize, orientation flip,
 * and returning to the app after the user changed the OS font setting
 * (visibilitychange / pageshow). Safe to call once at boot.
 */
export function initTextScaleCap(): void {
  if (typeof window === 'undefined') return;

  let raf = 0;
  const schedule = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      applyTextScaleCap();
    });
  };

  applyTextScaleCap();

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule();
  });
}
